import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Select,
  Spinner,
  Tooltip,
} from "@chakra-ui/react";
import { HiX } from "react-icons/hi";

const CodesetSelect = ({
  label,
  codesetCode,
  value,
  onChange,
  filter = null,
  size = "md",
  height = undefined,
  maxW = undefined,
  /** NEW: which field to return as `value.code` */
  codeField = "longCode", // "longCode" | "typeCode" | "camelCode"
  /** NEW: which field to show in the dropdown (falls back to codeField) */
  displayField = "description1",
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Guard: normalize field access safely
  const getSafe = (obj, key, fallback = "") =>
    obj && typeof obj === "object" && key in obj ? obj[key] ?? fallback : fallback;

  const getDisplayText = useCallback(
    (item) => {
      const display = String(getSafe(item, displayField, "")).trim();
      if (display) return display;
      const codeVal = String(getSafe(item, codeField, "")).trim();
      if (codeVal) return codeVal;
      // Final fallback chain to keep it robust
      return (
        getSafe(item, "description1") ||
        getSafe(item, "longCode") ||
        getSafe(item, "typeCode") ||
        getSafe(item, "camelCode") ||
        ""
      );
    },
    [displayField, codeField]
  );

  useEffect(() => {
    const fetchCodeset = async () => {
      setLoading(true);
      try {
        const url = new URL(`https://codex.mrc.ai/api/codelists/${codesetCode}`);
        if (filter) url.searchParams.set("filter", filter);

        const res = await fetch(url.toString());
        const json = await res.json();

        if (Array.isArray(json.codes)) {
          const sorted = [...json.codes].sort((a, b) =>
            getDisplayText(a).toLowerCase().localeCompare(getDisplayText(b).toLowerCase())
          );
          setOptions(sorted);
        } else {
          console.error("Unexpected response structure:", codesetCode, json);
          setOptions([]);
        }
      } catch (err) {
        console.error("Failed to fetch codeset:", err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCodeset();
  }, [codesetCode, filter, getDisplayText]);

  const handleChange = useCallback(
    (e) => {
      const selectedCodeValue = e.target.value;
      const selected = options.find((item) => String(getSafe(item, codeField)) === selectedCodeValue);
      if (selected) {
        onChange({
          code: String(getSafe(selected, codeField, "")),
          description: String(getSafe(selected, "description1", "")),
          codeListAgency: String(getSafe(selected, "codeListAgency", "")),
          codeListSource: String(getSafe(selected, "codeListSource", "")),
        });
      }
    },
    [options, onChange, codeField]
  );

  const handleClear = useCallback(() => {
    onChange({
      code: "",
      description: "",
      codeListAgency: "",
      codeListSource: "",
    });
  }, [onChange]);

  // Option key/value fallbacks to keep React happy even if a code field is missing
  const optionKeyFor = useCallback((item, idx) => {
    return (
      getSafe(item, "longCode") ||
      getSafe(item, "typeCode") ||
      getSafe(item, "camelCode") ||
      `opt-${idx}`
    );
  }, []);

  const selectValue = useMemo(() => value?.code || "", [value]);

  return (
    <FormControl maxW={maxW}>
      {label && <FormLabel fontSize={size}>{label}</FormLabel>}
      {loading ? (
        <Spinner />
      ) : (
        <HStack spacing={2}>
          <Select
            placeholder="Select..."
            value={selectValue}
            onChange={handleChange}
            size={size}
            height={height}
            title={`${value?.description || ""} ${value?.code || ""}`}
          >
            {options.map((item, idx) => {
              const key = optionKeyFor(item, idx);
              const val = String(getSafe(item, codeField, ""));
              const text = getDisplayText(item);
              return (
                <option key={key} value={val}>
                  {text}
                </option>
              );
            })}
          </Select>
          {selectValue && (
            <Tooltip label="Clear selection..." aria-label="Clear selection tooltip">
              <IconButton
                icon={<HiX />}
                size={size}
                variant="link"
                onClick={handleClear}
                aria-label="Clear"
              />
            </Tooltip>
          )}
        </HStack>
      )}
    </FormControl>
  );
};

export default CodesetSelect;
